import type { TFunction } from 'i18next';
import * as yup from 'yup';

import { simulationsEndpoints } from '@/api/endpoints';
import {
  parseStartingContext,
  STARTING_CONTEXT_ERROR_KEYS,
} from '@/modules/simulations/startingContext';
import { SCHEDULE_CADENCES, SIMULATION_CHANNEL_TYPES } from '@/modules/simulations/types';
import { SIMULATION_TEXT_MAX } from '@/shared/types/simulation-draft';
import { LENGTH_M, LENGTH_S } from '@/util/validation/constants';
import { createNameFieldSchema } from '@/util/validation/schema-builders';

export const createSimulationSchema = (t: TFunction, brainId: string, excludeName?: string) =>
  yup.object({
    name: createNameFieldSchema({
      getEndpoint: (name) => simulationsEndpoints.checkName(brainId, name),
      max: LENGTH_M,
      excludeName,
    }),
    scenario: yup
      .string()
      .trim()
      .required(t('validation.required'))
      .max(
        SIMULATION_TEXT_MAX,
        t('simulations.validation_scenario_max', { 0: SIMULATION_TEXT_MAX })
      ),
    pass_criteria: yup
      .string()
      .trim()
      .required(t('validation.required'))
      .max(
        SIMULATION_TEXT_MAX,
        t('simulations.validation_criteria_max', { 0: SIMULATION_TEXT_MAX })
      ),
    channel_type: yup.string().oneOf(SIMULATION_CHANNEL_TYPES).required().default('chat'),
    starting_context: yup
      .string()
      .default('')
      .test({
        name: 'starting-context',
        test(value, ctx) {
          const { error } = parseStartingContext(value);
          if (!error) {
            return true;
          }
          return ctx.createError({
            message: t(STARTING_CONTEXT_ERROR_KEYS[error.code], { 0: error.arg ?? '' }),
          });
        },
      }),
    category: yup
      .string()
      .trim()
      .max(LENGTH_S, t('simulations.validation_category_max', { 0: LENGTH_S }))
      .nullable()
      .default(null)
      .transform((v) => v || null),
    max_turns: yup.number().required().default(20),
    schedule_frequency: yup
      .string()
      .oneOf([...SCHEDULE_CADENCES])
      .nullable()
      .default(null)
      .transform((v) => v || null),
  });

export type SimulationFormValues = yup.InferType<ReturnType<typeof createSimulationSchema>>;
